import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import { RecordResponse } from '@/models/ServiceTypes';

export interface AdvisorGroupModuleLocalizationRecord {
  Key: string;
  Value: string;
}

/**
 * API for firm advisor group module localizations (renamable module names).
 * Key = Module.Name (e.g. "Portfolio Diversification"); Value = custom label (e.g. "Asset Allocation").
 * Revert to default: PUT with Value = Key (backend removes the override). No DELETE endpoint.
 */
export const advisorGroupModuleLocalizationsApi = createApi({
  reducerPath: 'advisorGroupModuleLocalizationsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['AdvisorGroupModuleLocalizations'],
  endpoints: (builder) => ({
    getAdvisorGroupModuleLocalizations: builder.query<
      AdvisorGroupModuleLocalizationRecord[],
      number
    >({
      query: (advisorGroupId) => ({
        url: `/api/firm/AdvisorGroups/${advisorGroupId}/ModuleLocalizations`,
        method: 'GET',
      }),
      transformResponse: (
        response: RecordResponse<AdvisorGroupModuleLocalizationRecord>,
      ) => {
        return response?.Records ?? [];
      },
      providesTags: (_result, _error, advisorGroupId) => [
        {
          type: 'AdvisorGroupModuleLocalizations',
          id: String(advisorGroupId),
        },
      ],
    }),

    updateAdvisorGroupModuleLocalization: builder.mutation<
      AdvisorGroupModuleLocalizationRecord,
      { advisorGroupId: number; Key: string; Value: string }
    >({
      query: ({ advisorGroupId, Key, Value }) => ({
        url: `/api/firm/AdvisorGroups/${advisorGroupId}/ModuleLocalizations/${encodeURIComponent(Key)}`,
        method: 'PUT',
        body: { AdvisorGroupId: advisorGroupId, Key, Value },
      }),
      invalidatesTags: (_result, _error, { advisorGroupId }) => [
        {
          type: 'AdvisorGroupModuleLocalizations',
          id: String(advisorGroupId),
        },
      ],
    }),

    createAdvisorGroupModuleLocalization: builder.mutation<
      AdvisorGroupModuleLocalizationRecord,
      { advisorGroupId: number; Key: string; Value: string }
    >({
      query: ({ advisorGroupId, Key, Value }) => ({
        url: `/api/firm/AdvisorGroups/${advisorGroupId}/ModuleLocalizations`,
        method: 'POST',
        body: { AdvisorGroupId: advisorGroupId, Key, Value },
      }),
      invalidatesTags: (_result, _error, { advisorGroupId }) => [
        {
          type: 'AdvisorGroupModuleLocalizations',
          id: String(advisorGroupId),
        },
      ],
    }),
  }),
});

export const {
  useGetAdvisorGroupModuleLocalizationsQuery,
  useUpdateAdvisorGroupModuleLocalizationMutation,
  useCreateAdvisorGroupModuleLocalizationMutation,
} = advisorGroupModuleLocalizationsApi;
